import type { TFunction } from 'i18next';

import {
  AffinityTarget,
  CarOwnershipStatus,
  ChargingStationAccess,
  DispatchMailFormat,
  fromDateTime,
  GenderTarget,
  HouseholdType,
  LanguageCodeEnum,
  GetAggregatedOnlineAdUnitDetailsLanguageInline,
  LifeStage,
  PropertyAttribute,
  StopStickerCriterion,
  WorkSituation,
  type DateTimeUnixType,
  type ManualSelectionDto,
  type PostCampaignDto,
  type PostalCodeDto,
  type PostalCodeRangeDto,
  type TargetGroupDto,
} from '@/services/ws-post/src/main/resources/rest-api';
import type { Decorator } from '@/js/libs/final-form';
import { createFinalFormDecorator } from '@/js/libs/scroll-to-error';

import { scrollTo } from 'src/helpers';
import type { Option } from 'src/types';
import {
  DEFAULT_POST_CODE_EXTENSION,
  getAgeOverviewNumericMap,
  HOUSEHOLD_SIZE_NUMERIC,
  INCOME_NUMERIC,
  MAX_LARGE_MAILING_WEIGHT,
  MAX_MEDIUM_MAILING_WEIGHT,
  MAX_STANDARD_MAILING_WEIGHT,
  MIN_MEDIUM_MAILING_WEIGHT,
  MIN_WEIGHT,
} from './constants';
import type { ManualSelectionType } from './containers/PostCampaignTargetGroupSettings/types';
import type { PaginatedItems } from './types';
import { PreviewDisabledReason } from './types';

export const mapI18nLanguageToLanguageCodeEnum = (language: string): LanguageCodeEnum => {
  const base = language.split('-')[0]?.toLowerCase() ?? '';

  switch (base) {
    case 'de':
      return LanguageCodeEnum.DE;
    case 'en':
      return LanguageCodeEnum.EN;
    case 'fr':
      return LanguageCodeEnum.FR;
    case 'it':
      return LanguageCodeEnum.IT;
    default:
      return LanguageCodeEnum.EN;
  }
};

// operationId-scoped inline enums give each operation its own nominal `LanguageInline` (all with the
// identical DE/EN/FR/IT members). This returns the aggregated-details variant; call sites for other
// operations bridge to their own variant with a cast (the values are the same).
export const mapI18nLanguageToLanguageInline = (language: string): GetAggregatedOnlineAdUnitDetailsLanguageInline => {
  switch (mapI18nLanguageToLanguageCodeEnum(language)) {
    case LanguageCodeEnum.DE:
      return GetAggregatedOnlineAdUnitDetailsLanguageInline.DE;
    case LanguageCodeEnum.EN:
      return GetAggregatedOnlineAdUnitDetailsLanguageInline.EN;
    case LanguageCodeEnum.FR:
      return GetAggregatedOnlineAdUnitDetailsLanguageInline.FR;
    case LanguageCodeEnum.IT:
      return GetAggregatedOnlineAdUnitDetailsLanguageInline.IT;
    default:
      return GetAggregatedOnlineAdUnitDetailsLanguageInline.EN;
  }
};

export const createStringOption = <TItem extends string>(item: TItem, t?: TFunction): Option<TItem> => ({
  label: t ? t(item) : item,
  value: item,
});

export const createScrollToErrorDecorator = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TFormValues extends Record<string, any>,
>(): Decorator<TFormValues, Partial<TFormValues>> => createFinalFormDecorator<TFormValues>({ scrollTo });

export const waitForUpdatedFormState = (callback: () => void) => setTimeout(callback, 0);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const emptyFn = (): any => undefined;

export function mapStringOptions<TItem extends string>(items: TItem[], t?: TFunction): Option<TItem>[];
export function mapStringOptions<TItem extends string>(response: PaginatedItems<TItem>, t?: TFunction): Option<TItem>[];
export function mapStringOptions<TItem extends string>(response: TItem[] | PaginatedItems<TItem>, t?: TFunction): Option<TItem>[] {
  const items = Array.isArray(response) ? response : response.items;

  return items.map((item) => createStringOption<TItem>(item as TItem, t));
}

export const formatStringOptions = <TItem extends string>(items: TItem[]): Option<TItem>[] => items.map((item) => createStringOption(item));

export const parseOption = <TItem>(option: Option<TItem>): TItem => option.value;

export const parseOptions = <TItem>(options: Option<TItem>[]): TItem[] => options.map(parseOption);

export const getRangePostCodesCount = (postCodeRange: PostalCodeRangeDto): number => {
  const { start, end } = postCodeRange;

  if (!start || !end) {
    return 0;
  }

  const diff = parseInt(end.code, 10) - parseInt(start.code, 10);

  return diff + 1;
};

export const getGrossPrice = (netPrice: number, discount: number): number => {
  const grossPrice = (100 * netPrice) / (100 - discount);

  return grossPrice;
};

const isReconciliationRequired = (postCampaign: PostCampaignDto | null): boolean => {
  const {
    hasAddressedMailingPositions,
    hasDigitalPositions,
    hasMailingPositions,
    hasEmailPositions,
    hasOnlinePositions,
    isAddressedMailingPositionsReconciled,
    isEmailPositionsReconciled,
    isDigitalPositionsReconciled,
    isMailingPositionsReconciled,
    isOnlinePositionsReconciled,
  } = postCampaign ?? {};

  const reconciliationRequired = [
    hasDigitalPositions && !isDigitalPositionsReconciled,
    hasMailingPositions && !isMailingPositionsReconciled,
    hasEmailPositions && !isEmailPositionsReconciled,
    hasAddressedMailingPositions && !isAddressedMailingPositionsReconciled,
    hasOnlinePositions && !isOnlinePositionsReconciled,
  ];

  return reconciliationRequired.some(Boolean);
};

export const getPreviewDisabledReason = (postCampaign: PostCampaignDto | null): PreviewDisabledReason | null => {
  const reconciliationRequired = isReconciliationRequired(postCampaign);
  const differentTaxRate = postCampaign?.isTaxRateDiffersInPositions;

  if (reconciliationRequired) {
    return PreviewDisabledReason.RECONCILIATION_REQUIRED;
  }

  if (differentTaxRate) {
    return PreviewDisabledReason.DIFFERENT_TAX_RATE;
  }

  return null;
};

const getWeightError = (t: TFunction, message: string): string => t('postBookingTool:ADDRESS_MAILING_WEIGHT_ERROR', { message });

export const validateWeight = (weight: number, format: DispatchMailFormat, t: TFunction): string | undefined => {
  if (format === DispatchMailFormat.STANDARD_LETTER && (weight < MIN_WEIGHT || weight > MAX_STANDARD_MAILING_WEIGHT)) {
    return getWeightError(t, t('messages:LIMIT_EXCEEDED_MIN_MAX', { min: MIN_WEIGHT, max: MAX_STANDARD_MAILING_WEIGHT }));
  }

  if (format === DispatchMailFormat.MIDI_LETTER && (weight < MIN_MEDIUM_MAILING_WEIGHT || weight > MAX_MEDIUM_MAILING_WEIGHT)) {
    return getWeightError(t, t('messages:LIMIT_EXCEEDED_MIN_MAX', { min: MIN_MEDIUM_MAILING_WEIGHT, max: MAX_MEDIUM_MAILING_WEIGHT }));
  }

  if (format === DispatchMailFormat.LARGE_FORMAT_LETTER && (weight < MIN_WEIGHT || weight > MAX_LARGE_MAILING_WEIGHT)) {
    return getWeightError(t, t('messages:LIMIT_EXCEEDED_MIN_MAX', { min: MIN_WEIGHT, max: MAX_LARGE_MAILING_WEIGHT }));
  }

  return undefined;
};

export const validateDispatchDate = (
  dispatchDate: DateTimeUnixType | null,
  initialDispatchDate: DateTimeUnixType | null,
  t: TFunction,
): string | undefined => {
  if (!dispatchDate) {
    return t('messages:FIELD_REQUIRED');
  }

  if (initialDispatchDate) {
    const reminderDispatchDateValue = fromDateTime(dispatchDate);
    const initialDispatchDateValue = fromDateTime(initialDispatchDate);

    if (reminderDispatchDateValue.isSameOrBefore(initialDispatchDateValue, 'day')) {
      return t('messages:DATE_MUST_BE_AFTER', { value: t('postBookingTool:INITIAL_DISPATCH_DATE').toLowerCase() });
    }
  }

  return undefined;
};

export const getAffinityLabelMap = (t: TFunction) => ({
  [AffinityTarget.CAR_MOTORBIKE]: t('postBookingTool:CAR_MOTORBIKE'),
  [AffinityTarget.BANKING_FINANCE]: t('postBookingTool:BANKING_FINANCE'),
  [AffinityTarget.BEAUTY]: t('postBookingTool:BEAUTY'),
  [AffinityTarget.DECORATION]: t('postBookingTool:DECORATION'),
  [AffinityTarget.TECHNOLOGY_COMPUTERS_CONSUMER_ELECTRONICS]: t('postBookingTool:TECHNOLOGY_COMPUTERS_CONSUMER_ELECTRONICS'),
  [AffinityTarget.PARENTING]: t('postBookingTool:PARENTING'),
  [AffinityTarget.PHOTOGRAPHY]: t('postBookingTool:PHOTOGRAPHY'),
  [AffinityTarget.GARDENING_BALCONY]: t('postBookingTool:GARDENING_BALCONY'),
  [AffinityTarget.LEISURE_HANDIWORK]: t('postBookingTool:LEISURE_HANDIWORK'),
  [AffinityTarget.HOMEOWNER]: t('postBookingTool:HOMEOWNER'),
  [AffinityTarget.DO_IT_YOURSELF]: t('postBookingTool:DO_IT_YOURSELF'),
  [AffinityTarget.PETS]: t('postBookingTool:PETS'),
  [AffinityTarget.COFFEE_TEA]: t('postBookingTool:COFFEE_TEA'),
  [AffinityTarget.COOKING]: t('postBookingTool:COOKING'),
  [AffinityTarget.BOOKS_LITERATURE]: t('postBookingTool:BOOKS_LITERATURE'),
  [AffinityTarget.JEWELRY]: t('postBookingTool:JEWELRY'),
  [AffinityTarget.GLASSES_SUNGLASSES]: t('postBookingTool:GLASSES_SUNGLASSES'),
  [AffinityTarget.TOYS_GAMES]: t('postBookingTool:TOYS_GAMES'),
  [AffinityTarget.SPORTS]: t('postBookingTool:SPORTS'),
  [AffinityTarget.BICYCLE]: t('postBookingTool:BICYCLE'),
  [AffinityTarget.MUSIC_VIDEO_MOVIES]: t('postBookingTool:MUSIC_VIDEO_MOVIES'),
  [AffinityTarget.VINTAGE_SECOND_HAND]: t('postBookingTool:VINTAGE_SECOND_HAND'),
  [AffinityTarget.WINE_DRINKS]: t('postBookingTool:WINE_DRINKS'),
  [AffinityTarget.FASHION_STYLE]: t('postBookingTool:FASHION_STYLE'),
  [AffinityTarget.MOBILE_PHONE]: t('postBookingTool:MOBILE_PHONE'),
  [AffinityTarget.FOOD_RETAIL]: t('postBookingTool:FOOD_RETAIL'),
  [AffinityTarget.FOOD_RESTAURANTS]: t('postBookingTool:FOOD_RESTAURANTS'),
  [AffinityTarget.ALTERNATIVE_FOOD_BIO]: t('postBookingTool:ALTERNATIVE_FOOD_BIO'),
  [AffinityTarget.FURNITURE]: t('postBookingTool:FURNITURE'),
  [AffinityTarget.NON_PROFIT_ORGANIZATION]: t('postBookingTool:NON_PROFIT_ORGANIZATION'),
  [AffinityTarget.PHARMACY]: t('postBookingTool:PHARMACY'),
  [AffinityTarget.ECOMMERCE_MAIL_ORDER]: t('postBookingTool:ECOMMERCE_MAIL_ORDER'),
  [AffinityTarget.SUSTAINABILITY]: t('postBookingTool:SUSTAINABILITY'),
  [AffinityTarget.TRAVELING]: t('postBookingTool:TRAVELING'),
});

export const getLifeStageLabelMap = (t: TFunction) => ({
  [LifeStage.EMPTY_NESTERS]: t('postBookingTool:EMPTY_NESTERS'),
  [LifeStage.FAMILY_WITH_TODDLER]: t('postBookingTool:FAMILY_WITH_TODDLER'),
  [LifeStage.MOVED_OUT_OF_PARENTAL_HOME]: t('postBookingTool:MOVED_OUT_OF_PARENTAL_HOME'),
  [LifeStage.RETIREMENT_PLANNING]: t('postBookingTool:RETIREMENT_PLANNING'),
  [LifeStage.SENIOR_HOUSING]: t('postBookingTool:SENIOR_HOUSING'),
});

export const getCarOwnershipStatusLabelMap = (t: TFunction) => ({
  [CarOwnershipStatus.NEWLY_PURCHASED]: t('postBookingTool:NEWLY_PURCHASED'),
  [CarOwnershipStatus.PURCHASED_1_3_YEARS_AGO]: t('postBookingTool:PURCHASED_1_3_YEARS_AGO'),
  [CarOwnershipStatus.PURCHASED_3_6_YEARS_AGO]: t('postBookingTool:PURCHASED_3_6_YEARS_AGO'),
  [CarOwnershipStatus.PURCHASED_LONG_AGO]: t('postBookingTool:PURCHASED_LONG_AGO'),
  [CarOwnershipStatus.NO_CAR_OWNER]: t('postBookingTool:NO_CAR_OWNER'),
});

export const getChargingStationAccessLabelMap = (t: TFunction) => ({
  [ChargingStationAccess.WITH_ACCESS]: t('postBookingTool:WITH_ACCESS'),
  [ChargingStationAccess.WITHOUT_ACCESS]: t('postBookingTool:WITHOUT_ACCESS'),
});

export const getPropertyAttributeLabelMap = (t: TFunction) => ({
  [PropertyAttribute.FLAT]: t('postBookingTool:FLAT'),
  [PropertyAttribute.HOUSE]: t('postBookingTool:HOUSE'),
  [PropertyAttribute.GARDEN_BALCONY_TERRACE]: t('postBookingTool:GARDEN_BALCONY_TERRACE'),
  [PropertyAttribute.NO_GARDEN_BALCONY_TERRACE]: t('postBookingTool:NO_GARDEN_BALCONY_TERRACE'),
  [PropertyAttribute.OWNER]: t('postBookingTool:OWNER'),
  [PropertyAttribute.RENTER]: t('postBookingTool:RENTER'),
});

export const getWorkSituationLabelMap = (t: TFunction) => ({
  [WorkSituation.FULL_TIME]: t('postBookingTool:FULL_TIME'),
  [WorkSituation.PART_TIME]: t('postBookingTool:PART_TIME'),
  [WorkSituation.INDEPENDENT]: t('postBookingTool:INDEPENDENT'),
  [WorkSituation.RETIRED]: t('postBookingTool:RETIRED'),
  [WorkSituation.STUDENT]: t('postBookingTool:STUDENT'),
  [WorkSituation.HOMEKEEPER]: t('postBookingTool:HOMEKEEPER'),
  [WorkSituation.UNEMPLOYED]: t('postBookingTool:UNEMPLOYED'),
});

export const getHouseholdTypeLabelMap = (t: TFunction) => ({
  [HouseholdType.SINGLE_PARENT]: t('postBookingTool:SINGLE_PARENT'),
  [HouseholdType.FAMILY_WITHOUT_KIDS]: t('postBookingTool:FAMILY_WITHOUT_KIDS'),
  [HouseholdType.FAMILY_WITH_KIDS]: t('postBookingTool:FAMILY_WITH_KIDS'),
  [HouseholdType.SHARED_FLAT]: t('postBookingTool:SHARED_FLAT'),
  [HouseholdType.FULL_NESTERS]: t('postBookingTool:FULL_NESTERS'),
});

export const getStopStickerCriterionLabelMap = (t: TFunction) => ({
  [StopStickerCriterion.WITH_STOP_STICKER]: t('postBookingTool:WITH_STOP_STICKER'),
  [StopStickerCriterion.WITHOUT_STOP_STICKER]: t('postBookingTool:WITHOUT_STOP_STICKER'),
  [StopStickerCriterion.ANY]: t('postBookingTool:BOTH'),
});

type FormattedValueFormatter = (options: { format: 'integer'; value: number }) => string;

export type TargetGroupCalculationEntry = {
  label: string;
  value: string;
};

type GetTargetGroupDisplayOptions = {
  t: TFunction;
  formatter: FormattedValueFormatter;
  isPathApiEnabled: boolean;
};

export const joinLabels = <TItem extends string>(values: TItem[] | undefined, labelMap: Record<TItem, string>): string | undefined =>
  values?.length ? values.map((value) => labelMap[value]).join(', ') : undefined;

export const getAffinitiesText = (affinities: TargetGroupDto['affinities'], t: TFunction): string | undefined => {
  const { foodAndLeisureAffinities = [], lifestyleAndHomeAffinities = [], technologyAndEverydayServicesAffinities = [] } = affinities ?? {};
  const pathAffinities = [...lifestyleAndHomeAffinities, ...foodAndLeisureAffinities, ...technologyAndEverydayServicesAffinities];

  return joinLabels(pathAffinities, getAffinityLabelMap(t));
};

export const getGendersText = (t: TFunction, genders: GenderTarget[] = []): string | undefined => {
  if (genders.includes(GenderTarget.FEMALE) && genders.includes(GenderTarget.MALE)) {
    return t('postBookingTool:OVERVIEW_TARGET_GROUP_GENDER_ALL');
  }

  if (genders.includes(GenderTarget.FEMALE)) {
    return t('postBookingTool:OVERVIEW_TARGET_GROUP_GENDER_FEMALE');
  }

  if (genders.includes(GenderTarget.MALE)) {
    return t('postBookingTool:OVERVIEW_TARGET_GROUP_GENDER_MALE');
  }

  return undefined;
};

export const getNumericRangeValue = <TItem extends string>(
  items: TItem[] | undefined,
  numericMap: Record<TItem, number>,
  maxUnlimited: boolean,
  formatNumber: (value: number) => string,
): string | undefined => {
  if (!items?.length) {
    return undefined;
  }

  const numericValues = items.map((item) => numericMap[item]);

  const from = Math.min(...numericValues);
  const to = Math.max(...numericValues);
  const maxValue = Math.max(...Object.values<number>(numericMap));
  const formattedTo = formatNumber(to);
  const fromText = formatNumber(from);
  const toText = to === maxValue && maxUnlimited ? `${formattedTo}+` : formattedTo;

  if (from === to) {
    return toText;
  }

  return `${fromText} - ${toText}`;
};

export const getNumericRangeText = <TItem extends string>(
  items: TItem[] | undefined,
  numericMap: Record<TItem, number>,
  translate: (value: string) => string,
  maxUnlimited = false,
  formatNumber = (value: number) => String(value),
): string | undefined => {
  const value = getNumericRangeValue(items, numericMap, maxUnlimited, formatNumber);

  return value ? translate(value) : undefined;
};

const getCalculationPostCodeLabel = (postCode?: PostalCodeDto): string => {
  if (!postCode) {
    return '—';
  }

  const { code, extension } = postCode;

  return extension === DEFAULT_POST_CODE_EXTENSION ? code : `${code}${extension}`;
};

const getCalculationPostCodeRangeLabel = ({ start, end }: PostalCodeRangeDto) =>
  `${getCalculationPostCodeLabel(start)} - ${getCalculationPostCodeLabel(end)}`;

const getCalculationGeographyTypeLabel = (type: ManualSelectionType, t: TFunction): string => {
  switch (type) {
    case 'cantons':
      return t('postBookingTool:MANUAL_ITEM_CANTON');
    case 'placenames':
      return t('postBookingTool:MANUAL_ITEM_PLACENAME');
    case 'languageRegions':
      return t('postBookingTool:MANUAL_ITEM_LANGUAGE_REGION');
    case 'livingEnvironments':
      return t('postBookingTool:MANUAL_ITEM_LIVING_ENVIRONMENT');
    case 'postalCodes':
    case 'postalCodeRanges':
      return t('postBookingTool:MANUAL_ITEM_POST_CODE');
    default:
      return '—';
  }
};

const getCalculationGeographyValueLabel = (
  type: ManualSelectionType,
  value: string | PostalCodeDto | PostalCodeRangeDto,
  t: TFunction,
): string => {
  switch (type) {
    case 'cantons':
    case 'placenames':
      return value as string;
    case 'languageRegions':
    case 'livingEnvironments':
      return t(value as string);
    case 'postalCodes':
      return getCalculationPostCodeLabel(value as PostalCodeDto);
    case 'postalCodeRanges':
      return getCalculationPostCodeRangeLabel(value as PostalCodeRangeDto);
    default:
      return '—';
  }
};

const getTargetGroupGeographyEntries = (manualSelection: ManualSelectionDto | undefined, t: TFunction): TargetGroupCalculationEntry[] => {
  if (!manualSelection) {
    return [];
  }

  const {
    cantons = [],
    languageRegions = [],
    livingEnvironments = [],
    placenames = [],
    postalCodeRanges = [],
    postalCodes = [],
  } = manualSelection;

  const postCodeValues = [
    ...postalCodes.map((value) => getCalculationGeographyValueLabel('postalCodes', value, t)),
    ...postalCodeRanges.map((value) => getCalculationGeographyValueLabel('postalCodeRanges', value, t)),
  ];

  const geographyGroups: [ManualSelectionType, (string | PostalCodeDto | PostalCodeRangeDto)[]][] = [
    ['cantons', cantons],
    ['languageRegions', languageRegions],
    ['livingEnvironments', livingEnvironments],
    ['placenames', placenames],
  ];

  const entries = geographyGroups
    .map(([type, values]) => {
      if (!values.length) {
        return undefined;
      }

      return {
        label: getCalculationGeographyTypeLabel(type, t),
        value: values.map((value) => getCalculationGeographyValueLabel(type, value, t)).join(', '),
      };
    })
    .filter((entry): entry is TargetGroupCalculationEntry => Boolean(entry));

  if (postCodeValues.length) {
    entries.push({
      label: getCalculationGeographyTypeLabel('postalCodes', t),
      value: postCodeValues.join(', '),
    });
  }

  return entries;
};

export const getTargetGroupFields = (
  targetGroup: TargetGroupDto,
  { t, formatter, isPathApiEnabled }: GetTargetGroupDisplayOptions,
): TargetGroupCalculationEntry[] => {
  const {
    affinities,
    ages,
    carOwnershipOption,
    chargingStationOption,
    genders,
    householdSizes,
    householdTypes,
    incomes,
    languages,
    lifeStages,
    propertyAttributes,
    stopStickerOption,
    workSituations,
  } = targetGroup;

  const gendersText = getGendersText(t, genders);
  const languagesText = languages?.length ? languages.map((language) => t(language)).join(', ') : undefined;
  const agesText = getNumericRangeText(
    ages,
    getAgeOverviewNumericMap(isPathApiEnabled),
    (value) => t('postBookingTool:OVERVIEW_TARGET_GROUP_AGE', { value }),
    true,
    (value: number) => formatter({ format: 'integer', value }),
  );
  const incomesText = getNumericRangeText(
    incomes,
    INCOME_NUMERIC,
    (value) => t('postBookingTool:OVERVIEW_TARGET_GROUP_INCOME', { value }),
    true,
    (value: number) => formatter({ format: 'integer', value }),
  );
  const householdSizesText = getNumericRangeText(
    householdSizes,
    HOUSEHOLD_SIZE_NUMERIC,
    (value) => t('postBookingTool:OVERVIEW_TARGET_GROUP_HOUSEHOLD', { value }),
    false,
    (value: number) => formatter({ format: 'integer', value }),
  );
  const affinitiesText = getAffinitiesText(affinities, t);
  const lifeStagesText = joinLabels(lifeStages, getLifeStageLabelMap(t));
  const carOwnershipText =
    carOwnershipOption?.enabled && carOwnershipOption.carOwnershipStatuses?.length
      ? joinLabels(carOwnershipOption.carOwnershipStatuses, getCarOwnershipStatusLabelMap(t))
      : undefined;
  const chargingStationText =
    chargingStationOption?.enabled && chargingStationOption.chargingStationAccess
      ? getChargingStationAccessLabelMap(t)[chargingStationOption.chargingStationAccess]
      : undefined;
  const propertyAttributesText = joinLabels(propertyAttributes, getPropertyAttributeLabelMap(t));
  const workSituationsText = joinLabels(workSituations, getWorkSituationLabelMap(t));
  const householdTypesText = joinLabels(householdTypes, getHouseholdTypeLabelMap(t));
  const stopStickerText =
    stopStickerOption?.enabled && stopStickerOption.stopStickerCriterion
      ? getStopStickerCriterionLabelMap(t)[stopStickerOption.stopStickerCriterion]
      : undefined;

  const entries: (TargetGroupCalculationEntry | undefined)[] = [
    gendersText ? { label: t('common:FIELD_GENDER'), value: gendersText } : undefined,
    languagesText ? { label: t('common:LANGUAGE'), value: languagesText } : undefined,
    agesText ? { label: t('postBookingTool:AGE'), value: agesText } : undefined,
    incomesText ? { label: t('postBookingTool:INCOME'), value: incomesText } : undefined,
    householdSizesText ? { label: t('postBookingTool:HOUSEHOLD_SIZE'), value: householdSizesText } : undefined,
    affinitiesText ? { label: t('postBookingTool:AFFINITY'), value: affinitiesText } : undefined,
    lifeStagesText ? { label: t('postBookingTool:LIFE_PHASE'), value: lifeStagesText } : undefined,
    carOwnershipText ? { label: t('postBookingTool:CAR_OWNERSHIP'), value: carOwnershipText } : undefined,
    chargingStationText ? { label: t('postBookingTool:CHARGING_STATION_ACCESS'), value: chargingStationText } : undefined,
    propertyAttributesText ? { label: t('postBookingTool:REAL_ESTATE'), value: propertyAttributesText } : undefined,
    workSituationsText ? { label: t('postBookingTool:OCCUPATION'), value: workSituationsText } : undefined,
    householdTypesText ? { label: t('postBookingTool:HOUSEHOLD_TYPE'), value: householdTypesText } : undefined,
    stopStickerText ? { label: t('postBookingTool:STOP_STICKER_CRITERION'), value: stopStickerText } : undefined,
  ];

  return entries.filter((entry): entry is TargetGroupCalculationEntry => Boolean(entry));
};

export const getTargetGroupCalculationEntries = (
  targetGroup: TargetGroupDto,
  options: GetTargetGroupDisplayOptions,
): TargetGroupCalculationEntry[] => [
  ...getTargetGroupFields(targetGroup, options),
  ...getTargetGroupGeographyEntries(targetGroup.geographicCriteria?.manualSelection, options.t),
];
